import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
    CRM_DROPDOWN_MAX_OPTIONS,
    CRM_OTHER_DETAILS_MAX_LENGTH,
} from '../../../common/config';
import { Dropdown } from '../../../components/Dropdown';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { Input } from '../../../components/Input';
import { InputGroup } from '../../../components/InputGroup';
import { RadioGroup } from '../../../components/RadioGroup';
import { Textarea } from '../../../components/Textarea';
import { ToggleButton } from '../../../components/ToggleButton';
import {
    DropdownOption,
    DropdownOptionsWithUndefined,
} from '../../../lib/Dropdown/types';
import { noOp } from '../../../lib/utils';
import { StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';
import {
    SetValueAction,
    handleConditionalLayout,
    handleFallbackFormulationDispatches,
    radioOptions,
} from '../StepColour/StepColour';
import { onOtherChange } from '../StepRestrictions/StepRestrictions';
import { fragranceCMROptions, fragranceColourConceptOptions } from './';
import { fragranceDirectionOptions } from './direction';
import { fragranceMustOptions } from './mustComplyTo';
import { FragranceOption } from './types';

export const StepFragrance: FC<StepProps> = ({
  // mandatoryFields,
  onValidate = noOp,
}) => {
  const { t } = useTranslation();
  const [
    {
      fragranceConcept,
      fragranceDirection,
      fragranceMust,
      fragranceMustOther,
      fragranceCMR,
      fragranceMaxAmountAllergens,
      fragranceRequestedApplicationDosage,
      fragranceSamples,
      fragranceRequiredSampleTime,
      fragranceLargerSample,
    },
    {
      setFragranceConcept,
      setFragranceDirection,
      setFragranceMust,
      setFragranceMustOther,
      setFragranceCMR,
      setFragranceMaxAmountAllergens,
      setFragranceRequestedApplicationDosage,
      setFragranceSamples,
      setFragranceRequiredSampleTime,
      setFragranceLargerSample,
    },
  ] = useBriefingState();
  const dispatch = useDispatch();

  const [isFragranceMustValid, setIsFragranceMustValid] = useState(true);
  const isStepValid = useMemo(
    () => isFragranceMustValid,
    [isFragranceMustValid],
  );
  const fragranceConfirmOptions = [
    t('topics:fragrance:options:confirmFragranceOptions:yes'),
    t('topics:fragrance:options:confirmFragranceOptions:no'),
  ];

  const { toggleStateValues, statefulYes } =
    handleConditionalLayout(fragranceSamples);
  const [showFragranceDetails, setShowFragranceDetails] = useState(statefulYes);
  const [toggleValue, setToggleValue] = useState<string | null>(
    fragranceSamples === null ? null : toggleStateValues,
  );
  const [fragranceSamplesNeeded, setFragranceSamplesNeeded] = useState(false);

  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);
  useEffect(() => {
    setFragranceSamplesNeeded(fragranceSamples === 'Yes');
  }, [fragranceSamples]);

  const getTranslatedDropdownName = useCallback(
    (options: FragranceOption[]) =>
      options.map((item) => ({
        name: t(`${item.name}`),
        value: item.value,
      })),
    [t],
  );

  const availableFragranceMustOptions =
    getTranslatedDropdownName(fragranceMustOptions);
  const availableColourConceptOptions = getTranslatedDropdownName(
    fragranceColourConceptOptions,
  );
  const availableDirectionOptions = getTranslatedDropdownName(
    fragranceDirectionOptions,
  );

  const onDropDownChange = useCallback(
    (
      selectedOption: DropdownOptionsWithUndefined,
      setAttribute: ActionCreatorWithPayload<DropdownOption[] | null>,
      setAttributeOther?: ActionCreatorWithPayload<string | null>,
    ) => {
      dispatch(setAttribute(selectedOption as DropdownOption[]));
      if (
        setAttributeOther &&
        (!selectedOption ||
          (Array.isArray(selectedOption) &&
            !selectedOption?.map((option) => option.value).includes('other')))
      ) {
        dispatch(setAttributeOther(null));
      }
    },
    [dispatch],
  );

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        margin={0}
        text={t('topics:fragrance:heading:general')}
        optionalField={false}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <TemplateFlexGridContainer.BriefingStepSection>
          <Heading
            level="h3"
            margin={0}
            text={t('topics:fragrance:fragranceFormulation')}
          />
          <div className="w-ful">
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {fragranceConfirmOptions.map((option) => {
                return (
                  <div key={option.toLowerCase()}>
                    <ToggleButton
                      dataTest={`fragrance-button-${option}`}
                      text={option}
                      value={option.toLowerCase()}
                      on={toggleValue === option}
                      onToggle={(on: boolean) => {
                        handleFallbackFormulationDispatches(
                          on,
                          option,
                          setShowFragranceDetails,
                          setToggleValue,
                          dispatch,
                          [
                            setFragranceConcept,
                            setFragranceDirection,
                            setFragranceMust,
                            setFragranceCMR,
                            setFragranceMaxAmountAllergens,
                            setFragranceRequestedApplicationDosage,
                            setFragranceSamples,
                            setFragranceRequiredSampleTime,
                            setFragranceLargerSample,
                          ] as SetValueAction[],
                        );
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </TemplateFlexGridContainer.BriefingStepSection>

        {showFragranceDetails && (
          <div
            data-testid="conditional-fragrance-content"
            className="space-y-4"
          >
            <TemplateFlexGridContainer.BriefingStepSection>
              <Heading
                level="h3"
                margin={0}
                text={t('topics:fragrance:attributesFulfill')}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="w-full md:w-10/12">
                  <InputGroup label={t('topics:fragrance:colourOrConcept')}>
                    <Dropdown
                      initialSelection={fragranceConcept || []}
                      options={availableColourConceptOptions || []}
                      error={!availableColourConceptOptions}
                      multiselect={true}
                      maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                      onChange={(selectedOption) => {
                        onDropDownChange(selectedOption, setFragranceConcept);
                      }}
                    />
                    <p className="mt-1 text-gray-600 text-2xs font-regular">
                      {t('labels:multiSelectHint')}
                    </p>
                  </InputGroup>
                </div>

                <div className="w-full md:w-10/12">
                  <InputGroup label={t('topics:fragrance:direction')}>
                    <Dropdown
                      initialSelection={fragranceDirection || []}
                      options={availableDirectionOptions || []}
                      error={!availableDirectionOptions}
                      multiselect={true}
                      maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                      onChange={(selectedOption) => {
                        onDropDownChange(selectedOption, setFragranceDirection);
                      }}
                    />
                    <p className="mt-1 text-gray-600 text-2xs font-regular">
                      {t('labels:multiSelectHint')}
                    </p>
                  </InputGroup>
                </div>
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>

            <TemplateFlexGridContainer.BriefingStepSection>
              <Heading
                level="h3"
                margin={0}
                text={t('topics:fragrance:requirements')}
                optionalField={true}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="w-full md:w-10/12">
                  <InputGroup label={t('topics:fragrance:mustComply')}>
                    <Dropdown
                      initialSelection={fragranceMust || []}
                      options={availableFragranceMustOptions || []}
                      error={!availableFragranceMustOptions}
                      multiselect={true}
                      maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                      onChange={(selectedOption) => {
                        onDropDownChange(
                          selectedOption,
                          setFragranceMust,
                          setFragranceMustOther,
                        );
                      }}
                    />
                    <p className="mt-1 text-gray-600 text-2xs font-regular">
                      {t('labels:multiSelectHint')}
                    </p>
                  </InputGroup>

                  {(fragranceMust || []).some(
                    (option) => option && option.value === 'other',
                  ) && (
                    <div className="mt-3">
                      <InputGroup
                        label={t('topics:fragrance:specifyFragranceMustOther')}
                      >
                        <div className="w-full __specification__ h-32]">
                          <Textarea
                            placeholder={t('placeholders:textareaDefault')}
                            initialValue={fragranceMustOther || undefined}
                            onChange={(e: ChangeEvent) =>
                              onOtherChange(dispatch, e, setFragranceMustOther)
                            }
                            onValidate={setIsFragranceMustValid}
                            maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
                          />
                        </div>
                      </InputGroup>
                    </div>
                  )}
                </div>

                <div className="w-full md:w-10/12">
                  <InputGroup label={t('topics:fragrance:CMR')}>
                    <Dropdown
                      initialSelection={
                        fragranceCMR
                          ? {
                              name: fragranceCMR,
                              value: fragranceCMR.toLowerCase(),
                            }
                          : undefined
                      }
                      options={
                        getTranslatedDropdownName(fragranceCMROptions) || []
                      }
                      error={!getTranslatedDropdownName(fragranceCMROptions)}
                      multiselect={false}
                      maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const optionName = Array.isArray(selectedOption)
                            ? selectedOption[0].name
                            : selectedOption?.name;
                          if (optionName) {
                            dispatch(setFragranceCMR(optionName));
                          }
                        }
                      }}
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="w-full md:w-8/12">
                <InputGroup label={t('topics:fragrance:maxAllergensOnLabel')}>
                  <Input
                    initialValue={fragranceMaxAmountAllergens}
                    type="text"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      dispatch(
                        setFragranceMaxAmountAllergens(e.target.value),
                      );
                    }}
                  />
                </InputGroup>
              </div>

              <div className="w-full md:w-8/12">
                <InputGroup
                  label={t('topics:fragrance:requestedApplicationDosage')}
                >
                  <Input
                    initialValue={fragranceRequestedApplicationDosage}
                    type="text"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      dispatch(
                        setFragranceRequestedApplicationDosage(
                          e.target.value,
                        ),
                      );
                    }}
                  />
                </InputGroup>
              </div>

              <div className="w-full md:w-8/12">
                <RadioGroup
                  label={t('topics:fragrance:fragranceSamples')}
                  onChange={(selectedValue: string | null) => {
                    dispatch(setFragranceSamples(selectedValue));
                  }}
                  initialSelection={fragranceSamples}
                  radioOptions={radioOptions(
                    t,
                    'fragrance',
                    'confirmFragranceOptions',
                  )}
                />
              </div>

              {fragranceSamplesNeeded && (
                <>
                  <div className="w-full md:w-8/12">
                    <InputGroup
                      label={t('topics:fragrance:requiredSampleTime:heading')}
                      optional={true}
                    >
                      <Input
                        initialValue={fragranceRequiredSampleTime}
                        type="text"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          dispatch(
                            setFragranceRequiredSampleTime(e.target.value),
                          );
                        }}
                      />
                    </InputGroup>
                  </div>

                  <div className="w-full md:w-8/12">
                    <InputGroup
                      label={t(
                        'topics:fragrance:largerFragranceSize:heading',
                      )}
                      optional={true}
                    >
                      <Input
                        initialValue={fragranceLargerSample}
                        type="text"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          dispatch(setFragranceLargerSample(e.target.value));
                        }}
                      />
                    </InputGroup>
                  </div>
                </>
              )}
            </TemplateFlexGridContainer.BriefingStepSection>
          </div>
        )}
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
